import { TemplateSearchItem } from '@/components/confluence/templates/context/template-fuzzy-search-context.tsx';

import { Tracking } from '../../../../analytics/atl-analytics/tracking';

import type { AnalyticsData } from '../../../../analytics/atl-analytics/gasv3';

const PREFIX = 'wac.showroom.';

export class ConfluenceTemplateAnalytics {
	private static readonly LearnMore = PREFIX + 'learn-more';
	private static readonly ViewTemplates = PREFIX + 'view-templates';
	private static readonly ViewTemplateCategory = PREFIX + 'view-template-category';
	private static readonly ViewPartnerTemplate = PREFIX + 'view-partner-template';
	private static readonly SearchBack = PREFIX + 'search-back';
	private static readonly ViewAllCollections = PREFIX + 'view-all-more-collections';
	private static readonly ViewAllCollectionsBelong = PREFIX + 'view-all-more-collections-belong';
	private static readonly ViewAllTemplates = PREFIX + 'view-all-more-templates';
	private static readonly BackToGallery = PREFIX + 'back-to-gallery';
	private static readonly ViewTemplate = PREFIX + 'view-template';
	private static readonly ViewTemplateTaxonomyCategory = PREFIX + 'view-template-taxonomy-category';

	private static readonly DefaultProps = {
		'data-event-container': 'confluenceTemplates',
		'data-product-key': 'confluence',
	};

	/**
	 * when the user clicks learn more
	 * @param urlSlug
	 * @param cardVariant
	 */
	public static learnMoreButton(urlSlug?: string | null, cardVariant?: string | null) {
		// Example to list variant without firing experiment exposure
		// FeatureGates.getExperimentValue('experiment-key', 'variant', false, {
		//     fireExperimentExposure: false
		// });

		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-cs-override-id': `template-card-overlay-${urlSlug}`,
			'data-event-component': 'linkButton',
			'data-template-key': urlSlug,
			'data-label': 'learn-more',
			'data-track-event': ConfluenceTemplateAnalytics.LearnMore,
			'data-card-variant': cardVariant ?? 'modal',
		};
	}

	/**
	 * when the user views a collection
	 * @param urlSlug
	 */
	public static viewTemplatesCollection(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-collection-key': urlSlug,
			'data-label': 'view-templates',
			'data-track-event': ConfluenceTemplateAnalytics.ViewTemplates,
		};
	}

	/**
	 * when the user selects a category to view templates
	 * @param urlSlug
	 */
	public static viewTemplateCategory(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-category-key': urlSlug,
			'data-label': 'view-template-category',
			'data-track-event': ConfluenceTemplateAnalytics.ViewTemplateCategory,
		};
	}

	/**
	 * when the user clicks view template on a partner template
	 * @param urlSlug
	 */
	public static viewPartnerTemplate(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-template-key': urlSlug,
			'data-label': 'view-template',
			'data-track-event': ConfluenceTemplateAnalytics.ViewPartnerTemplate,
		};
	}

	/**
	 * when the user clicks back button
	 */
	public static searchBackButton() {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-label': 'search-back',
			'data-track-event': ConfluenceTemplateAnalytics.SearchBack,
		};
	}

	/**
	 * when the user clicks view all collections on collection page
	 */
	public static viewAllCollections() {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-label': 'view-all-collections',
			'data-track-event': ConfluenceTemplateAnalytics.ViewAllCollections,
		};
	}

	/**
	 * when the user clicks view all templates in category on template page
	 * @param urlSlug
	 */
	public static viewAllCategoryTemplates(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-category-key': urlSlug,
			'data-label': 'view-all-templates',
			'data-track-event': ConfluenceTemplateAnalytics.ViewAllTemplates,
		};
	}

	/**
	 * when the user clicks view all collections a template belongs to
	 */
	public static viewAllCollectionsBelong() {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-label': 'view-all-collections-belong',
			'data-track-event': ConfluenceTemplateAnalytics.ViewAllCollectionsBelong,
		};
	}

	/**
	 * when the user clicks back to gallery from a collection
	 */
	public static backToGalleryAnalyticProps() {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-label': 'back-to-gallery',
			'data-track-event': ConfluenceTemplateAnalytics.BackToGallery,
		};
	}

	/**
	 * when a clicks view template in a collection page
	 * @param urlSlug
	 */
	public static viewTemplateAnalyticProps(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-template-key': urlSlug,
			'data-label': 'view-template',
			'data-track-event': ConfluenceTemplateAnalytics.ViewTemplate,
		};
	}

	/**
	 * when the user navigates through the partner template carousel
	 * @param currentSlide
	 * @param nextSlide
	 * @param totalSlides
	 */
	public static sendPartnerCarouselAnalytic(
		currentSlide: number,
		nextSlide: number,
		totalSlides: number,
	) {
		// infinite so we will loop around so consider the direction
		const isRight =
			nextSlide > currentSlide || (nextSlide === 0 && currentSlide === totalSlides - 1);

		Tracking.trackEvent('wac.showroom.partner-template-carousel', {
			eventComponent: 'carouselItem',
			eventContainer: 'confluenceTemplates',
			carouselDirection: isRight ? 'right' : 'left',
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 * Fired when the user navigates through the collection carousel
	 * @param currentSlide
	 * @param nextSlide
	 * @param totalSlides
	 */
	public static sendCollectionCarouselAnalytic(
		currentSlide: number,
		nextSlide: number,
		totalSlides: number,
	) {
		// infinite so we will loop around so consider the direction
		const isRight =
			nextSlide > currentSlide || (nextSlide === 0 && currentSlide === totalSlides - 1);

		Tracking.trackEvent('wac.showroom.collection-carousel', {
			eventComponent: 'carouselItem',
			eventContainer: 'confluenceTemplates',
			carouselDirection: isRight ? 'right' : 'left',
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 *
	 * Fired when the user submits on a search
	 */
	public static sendTemplateSearchAnalytic() {
		Tracking.trackEvent('wac.showroom.template-search', {
			eventComponent: 'searchInput',
			eventContainer: 'confluenceTemplates',
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 * when the user enters data into search we want to see how many results are returned
	 */
	public static sendTemplateSearchResultsAnalytic(searchResults: string) {
		Tracking.trackEvent('wac.showroom.template-search-results', {
			eventComponent: 'search',
			eventContainer: 'confluenceTemplates',
			searchResults: searchResults,
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 * when the user enters data into search
	 */
	public static sendTemplateFuzzySearchAnalytic() {
		Tracking.trackEvent('wac.showroom.template-fuzzy-search', {
			eventComponent: 'fuzzySearchInput',
			eventContainer: 'confluenceTemplates',
		} as AnalyticsData & { [key: string]: string });
	}
	/**
	 * when the user clicks into search
	 */
	public static sendTemplateFuzzySearchFocusAnalytic() {
		Tracking.trackEvent('wac.showroom.template-fuzzy-search-focus', {
			eventComponent: 'fuzzySearchFocus',
			eventContainer: 'confluenceTemplates',
		} as AnalyticsData & { [key: string]: string });
	}
	/**
	 * when the user enters data into fuzzy search we want to see how many results are returned
	 */
	public static sendTemplateFuzzySearchResultsAnalytic(searchResults: string) {
		Tracking.trackEvent('wac.showroom.template-fuzzy-search-results', {
			eventComponent: 'fuzzySearch',
			eventContainer: 'confluenceTemplates',
			searchResults: searchResults,
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 * when the user clicks on any preview item
	 */
	public static sendTemplateFuzzySearchPreviewAnalytic(searchItem: TemplateSearchItem) {
		Tracking.trackEvent('wac.showroom.template-fuzzy-search-preview', {
			eventComponent: 'previewItem',
			eventContainer: 'confluenceTemplates',
			searchItemName: searchItem?.name ?? '',
			searchItemId: searchItem?.id ?? '',
			searchItemType: searchItem.type ?? '',
		} as AnalyticsData & { [key: string]: string });
	}

	/**
	 * when the user selects a category to view templates on unified page
	 * @param urlSlug
	 */
	public static viewTemplateFuzzySearchCategory(urlSlug?: string | null) {
		return {
			...ConfluenceTemplateAnalytics.DefaultProps,
			'data-event-component': 'linkButton',
			'data-category-key': urlSlug,
			'data-label': 'view-template-fuzzy-search-category',
			'data-track-event': ConfluenceTemplateAnalytics.ViewTemplateCategory,
		};
	}

	/**
	 * when the user selects a taxonomy category to view templates on unified page
	 * @param urlSlug
	 */
	public static viewTemplatesTaxonomyCategory(urlSlug?: string | null) {
		Tracking.trackEvent(ConfluenceTemplateAnalytics.ViewTemplateTaxonomyCategory, {
			...ConfluenceTemplateAnalytics.DefaultProps,
			eventComponent: 'linkButton',
			eventContainer: 'confluenceTemplates',
			categoryName: urlSlug,
		} as AnalyticsData & { [key: string]: string });
	}
}
